import { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Shirt, Star } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { OrderModal } from "./OrderModal";
import { SpecialOrderModal } from "./SpecialOrderModal";

const currentSeason = [
  { club: "Barcelona", country: "Spain", accent: "#A50044", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/kxfsucam_Barcellona%20Away.jpg" },
  { club: "Real Madrid", country: "Spain", accent: "#FEBE10", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/81dvq3pk_Real%20Madrid.jpg" },
  { club: "Paris Saint-Germain", country: "France", accent: "#004170", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/2aa9tyhn_PSG.jpg" },
  { club: "Bayern Munich", country: "Germany", accent: "#DC052D", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/tb6rugq3_Bayern%20Monaco.jpg" },
  { club: "Borussia Dortmund", country: "Germany", accent: "#FDE100", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/ffoz5yvy_Borussia%20Dortmund.jpg" },
  { club: "Juventus", country: "Italy", accent: "#FFFFFF", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/byfwvrlk_Juventus.jpg" },
  { club: "Inter", country: "Italy", accent: "#0068A8", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/fnnylpw1_Inter%20Milan.jpg" },
  { club: "AC Milan", country: "Italy", accent: "#FB090B", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/u9xj8ge9_Ac%20Milian%20Away.jpg" },
  { club: "AS Roma", country: "Italy", accent: "#8E1F2F", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/fa79jpfi_532254.png" },
  { club: "Atletico Madrid", country: "Spain", accent: "#CB3524", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/52wgvbvi_Atletico%20Madrid.jpg" },
  { club: "Manchester City", country: "England", accent: "#6CABDD", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/8j4y3qpl_Man%20City.jpg" },
  { club: "Arsenal", country: "England", accent: "#EF0107", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/qqc742bz_Arsenal.jpg" },
  { club: "Manchester United", country: "England", accent: "#DA291C", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/jl008t7v_Man%20UTD.jpg" },
];

const retroLegends = [
  { club: "Brazil", player: "Ronaldo", year: "2002", accent: "#FEDD00", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/ad336rj2_Ronaldo%20Brazil.jpg" },
  { club: "France", player: "Zidane", year: "1998", accent: "#002654", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/a8mv48lp_Zidane%20Francia.jpg" },
  { club: "Napoli", player: "Maradona", year: "1987", accent: "#12A0D7", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/ybwmzvyv_Maradona%20Napoli.webp" },
];

const kidsKits = [
  { club: "Barcelona", country: "Spain", accent: "#A50044", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/bzmhhjtn_Barcellona%20Bambino.jpg" },
  { club: "AC Milan", country: "Italy", accent: "#FB090B", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/ds7vm4k1_Milan%20Bambino.jpg" },
  { club: "Norway", country: "National Team", accent: "#EF2B2D", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/4cd65ouk_Norvegia%20Bambino.jpg" },
  { club: "AS Roma", country: "Italy · Retro", accent: "#8E1F2F", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/ysytv92q_Roma%20Retro%20Bambino.jpg" },
  { club: "Spain", country: "National Team", accent: "#AA151B", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/w9d00a5k_Spagna%20Bambino.png" },
];

const nbaTeams = [
  { club: "Los Angeles Lakers", country: "NBA · USA", accent: "#552583", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/8ox3310c_Lakers.webp" },
  { club: "Golden State Warriors", country: "NBA · USA", accent: "#1D428A", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/af9didq8_GoldenState.webp" },
  { club: "Boston Celtics", country: "NBA · USA", accent: "#007A33", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/g9mdp3gw_Boston.webp" },
  { club: "Cleveland Cavaliers", country: "NBA · USA", accent: "#860038", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/84dju8bd_Cleveland.jpg" },
  { club: "Philadelphia 76ers", country: "NBA · USA", accent: "#006BB6", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/voav2o2e_Philadelphia.webp" },
  { club: "Chicago Bulls", country: "NBA · USA", accent: "#CE1141", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/972d7enw_Chicago.jpg" },
  { club: "San Antonio Spurs", country: "NBA · USA", accent: "#C4CED4", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/2rgeext4_SanAntonio.jpg" },
  { club: "Toronto Raptors", country: "NBA · Canada", accent: "#753BBD", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/nlx69zmp_Toronto%20Raptors.webp" },
];

const f1Teams = [
  { club: "Scuderia Ferrari", country: "F1 · Polo", accent: "#DC0000", price: 60, cat: "f1polo", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/c7m4krcn_Ferrari%20Polo.jpg" },
  { club: "Ferrari Team Tee", country: "F1 · T-shirt", accent: "#DC0000", price: 50, cat: "f1tee", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/wvyyyufv_Ferrari%20TShirt.jpg" },
  { club: "McLaren", country: "F1 · Polo", accent: "#FF8000", price: 60, cat: "f1polo", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/2jw5ysbp_McLaren%20Polo.jpg" },
  { club: "Mercedes-AMG", country: "F1 · T-shirt", accent: "#27F4D2", price: 50, cat: "f1tee", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/watrftfn_Mercedes%20TShirt.jpg" },
  { club: "Aston Martin", country: "F1 · T-shirt", accent: "#00594F", price: 50, cat: "f1tee", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/4bxymnmc_AstonMartin%20TShirt.jpg" },
  { club: "Red Bull Racing", country: "F1 · Polo", accent: "#3671C6", price: 60, cat: "f1polo", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/qe98u671_RedBull%20Polo.jpg" },
];

const tracksuits = [
  { club: "Barcelona", country: "Spain", accent: "#A50044", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/5v2qh03o_Barcellona.jpg" },
  { club: "Liverpool", country: "England", accent: "#C8102E", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/zc5dx4kr_Liverpool.jpg" },
  { club: "Paris Saint-Germain", country: "France", accent: "#004170", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/hr9a6gm1_PSG.jpg" },
  { club: "Real Madrid", country: "Spain", accent: "#FEBE10", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/hfh145lx_Real%20Madrid.jpg" },
  { club: "Spain", country: "National Team · Retro", accent: "#AA151B", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/oh90hfwj_Spagna.jpg" },
];

const nbaLegends = [
  { club: "Michael Jordan", country: "USA Dream Team", accent: "#0A2240", year: "1992", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/27pwokjz_DREAM%20JORDAN.jpg" },
  { club: "Kobe Bryant", country: "LA Lakers", accent: "#552583", year: "2007-08", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/1sh6swoe_Kobe%20Lakers.jpg" },
  { club: "Larry Bird", country: "Boston Celtics", accent: "#007A33", year: "1985-86", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/jjnmtfhq_Larry%20Bird%20Boston.jpg" },
  { club: "Dennis Rodman", country: "Chicago Bulls", accent: "#CE1141", year: "1997-98", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/371gi8kc_Rodman%20Chicago.jpg" },
  { club: "Shaquille O'Neal", country: "Orlando Magic", accent: "#0057B8", year: "1994-95", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/m0qe8tjq_Shaq%20Orlando.jpg" },
  { club: "John Stockton", country: "Utah Jazz", accent: "#753BBD", year: "1991-92", image: "https://customer-assets-7cd3h4nn.emergentagent.net/job_emerald-collection-1/artifacts/2yluha2o_Stockton%20Utah.jpg" },
];

const JerseyCard = ({ item, index, inView, badge, badgeRight, subLabel, bottomLabel, price, onSelect, imgAlt }) => {
  const { t } = useLanguage();
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      onClick={() => onSelect && onSelect()}
      className="group relative bg-[#0A0A0A] border border-white/5 rounded-sm overflow-hidden hover:border-[#009A49]/40 transition-colors duration-300 cursor-pointer"
      data-testid={`jersey-card-${item.club.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {/* Jersey visual */}
      <div className="relative aspect-[4/5] flex items-center justify-center overflow-hidden bg-[#050505]">
        {item.image ? (
          <>
            {/* Real jersey image */}
            <img
              src={item.image}
              alt={imgAlt || `${item.club} jersey`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            {/* Subtle top gradient for depth */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
          </>
        ) : (
          <>
            {/* Accent glow */}
            <div 
              className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500"
              style={{ background: `radial-gradient(circle at 50% 40%, ${item.accent}, transparent 70%)` }}
            />
            
            {/* Vertical stripes texture */}
            <div className="absolute inset-0 opacity-[0.04]" style={{
              backgroundImage: `repeating-linear-gradient(90deg, ${item.accent} 0px, ${item.accent} 2px, transparent 2px, transparent 20px)`
            }} />
            
            {/* Shirt icon */}
            <motion.div
              className="relative z-10 flex flex-col items-center"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Shirt 
                className="w-20 h-20 md:w-24 md:h-24 transition-colors duration-300 text-zinc-700 group-hover:text-white" 
                strokeWidth={1}
              />
            </motion.div>
          </>
        )}
        
        {/* Top-left badge */}
        {badge && (
          <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 bg-[#009A49] rounded-sm">
            {(badge === t("gallery.retro") || badge === t("gallery.legend")) && <Star className="w-3 h-3 text-white fill-white" />}
            <span className="text-[10px] font-bold tracking-wider uppercase text-white font-body">{badge}</span>
          </div>
        )}
        
        {/* Top-right badge */}
        {badgeRight && (
          <div className="absolute top-3 right-3 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-sm">
            <span className="text-[10px] font-bold tracking-wider uppercase text-white font-body">{badgeRight}</span>
          </div>
        )}

        {/* Hover request overlay */}
        <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center pointer-events-none">
          <span className="px-4 py-2 border border-[#009A49] rounded-full text-[#009A49] font-body text-xs font-semibold tracking-wider uppercase">
            {t("gallery.requestCta")}
          </span>
        </div>
      </div>
      
      {/* Info */}
      <div className="p-4 border-t border-white/5">
        {subLabel && (
          <p className="text-[#009A49] font-body text-xs font-semibold tracking-wider uppercase mb-1">
            {subLabel}
          </p>
        )}
        <h4 className="font-heading text-lg text-white tracking-wide leading-tight">
          {item.club}
        </h4>
        <div className="flex items-center justify-between mt-2">
          <p className="text-zinc-600 font-body text-xs">{item.country || item.year}</p>
          {price ? (
            <p className="text-[#009A49] font-heading text-lg font-bold" data-testid="card-price">€{price}</p>
          ) : (
            <p className="text-zinc-500 font-body text-[10px] tracking-widest uppercase">
              {bottomLabel}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const GallerySection = ({ onSelectJersey }) => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05,
  });
  const [activeTab, setActiveTab] = useState("current");
  const [orderProduct, setOrderProduct] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [specialOpen, setSpecialOpen] = useState(false);

  const handleRequest = (_productName, categoryKey) => {
    // Jersey field left empty — the clicked card is only an example
    setOrderProduct({ jersey: "", category: categoryKey || "" });
    setModalOpen(true);
  };

  const handleCustomRequest = () => {
    // Fully empty form for the "Request Your Jersey" CTA
    setOrderProduct({ jersey: "", category: "" });
    setModalOpen(true);
  };

  return (
    <section 
      id="gallery" 
      className="py-24 md:py-32 bg-[#050505] relative"
      ref={ref}
      data-testid="gallery-section"
    >
      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      <div className="section-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#009A49] mb-6 font-body">
            {t("gallery.label")}
          </p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-wide">
              {t("gallery.heading")}
            </h2>
            <p className="text-zinc-500 font-body max-w-md">
              {t("gallery.description")}
            </p>
          </div>
          <div className="emerald-divider mt-8" />
          {/* SEO: squadre e categorie del catalogo — testo visibile, indicizzabile */}
          <p className="mt-6 text-zinc-600 font-body text-sm leading-relaxed max-w-3xl" data-testid="seo-teams-text">
            {t("seo.teamsText")}
          </p>
        </motion.div>

        {/* Catalog note — highlighted */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-12 relative overflow-hidden rounded-sm bg-[#009A49] shadow-[0_0_90px_rgba(0,154,73,0.4)]"
          data-testid="catalog-note"
        >
          {/* Sheen highlight */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/15 via-white/5 to-transparent pointer-events-none" />
          <div className="relative px-6 md:px-10 py-8 md:py-10 flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex items-start gap-4 md:gap-6 flex-1">
              <div className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-full bg-black flex items-center justify-center text-[#009A49] font-heading text-2xl md:text-4xl font-bold shadow-lg">
                !
              </div>
              <div className="flex-1">
                <p className="text-white font-heading text-base md:text-xl font-bold tracking-[0.25em] uppercase mb-2 md:mb-3">
                  {t("gallery.catalogHeading")}
                </p>
                <p className="text-white font-body text-lg md:text-xl lg:text-2xl leading-relaxed font-semibold">
                  {t("gallery.catalogNote")}
                </p>
              </div>
            </div>
            {/* CTA: Request your custom jersey */}
            <button
              onClick={handleCustomRequest}
              className="flex-shrink-0 bg-black hover:bg-zinc-900 text-white font-body font-bold text-sm md:text-base tracking-[0.2em] uppercase px-6 md:px-10 py-4 md:py-6 rounded-full transition-colors duration-300 shadow-xl whitespace-nowrap"
              data-testid="request-custom-jersey-btn"
            >
              {t("gallery.requestYours")}
            </button>
          </div>
        </motion.div>

        {/* Special order CTA — prominent, near the catalog */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mb-12 flex flex-col md:flex-row items-center justify-center md:justify-start gap-4 md:gap-6"
        >
          <button
            onClick={() => setSpecialOpen(true)}
            className="group border-2 border-[#009A49] text-[#009A49] hover:bg-[#009A49] hover:text-white font-body font-bold text-sm md:text-base tracking-[0.25em] uppercase px-10 md:px-14 py-5 md:py-6 rounded-full transition-colors duration-300 shadow-[0_0_40px_rgba(0,154,73,0.3)] hover:shadow-[0_0_60px_rgba(0,154,73,0.5)] flex items-center gap-3 flex-shrink-0"
            data-testid="special-order-btn"
          >
            <svg className="w-5 h-5 transition-transform duration-300 group-hover:rotate-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            {t("special.button")}
          </button>
          <p
            data-testid="special-order-hint"
            className="text-zinc-300 font-body text-base md:text-lg leading-relaxed max-w-md text-center md:text-left"
          >
            {t("special.hint")}
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-4 gap-2 mb-12 md:flex md:gap-4 md:flex-wrap md:justify-start"
        >
          <button
            onClick={() => setActiveTab("current")}
            className={`w-full md:w-auto text-center whitespace-nowrap overflow-hidden text-ellipsis px-2 py-2.5 md:px-6 md:py-3 rounded-full font-body text-[11px] md:text-sm font-semibold tracking-wider uppercase transition-colors duration-300 ${
              activeTab === "current"
                ? "bg-[#009A49] text-white"
                : "border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
            }`}
            data-testid="tab-current"
          >
            <><span className="sm:hidden">{t("gallery.tabCurrentShort")}</span><span className="hidden sm:inline">{t("gallery.tabCurrent")}</span></>
          </button>
          <button
            onClick={() => setActiveTab("retro")}
            className={`w-full md:w-auto text-center whitespace-nowrap overflow-hidden text-ellipsis px-2 py-2.5 md:px-6 md:py-3 rounded-full font-body text-[11px] md:text-sm font-semibold tracking-wider uppercase transition-colors duration-300 ${
              activeTab === "retro"
                ? "bg-[#009A49] text-white"
                : "border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
            }`}
            data-testid="tab-retro"
          >
            <><span className="sm:hidden">{t("gallery.tabRetroShort")}</span><span className="hidden sm:inline">{t("gallery.tabRetro")}</span></>
          </button>
          <button
            onClick={() => setActiveTab("kids")}
            className={`w-full md:w-auto text-center whitespace-nowrap overflow-hidden text-ellipsis px-2 py-2.5 md:px-6 md:py-3 rounded-full font-body text-[11px] md:text-sm font-semibold tracking-wider uppercase transition-colors duration-300 ${
              activeTab === "kids"
                ? "bg-[#009A49] text-white"
                : "border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
            }`}
            data-testid="tab-kids"
          >
            <><span className="sm:hidden">{t("gallery.tabKidsShort")}</span><span className="hidden sm:inline">{t("gallery.tabKids")}</span></>
          </button>
          <button
            onClick={() => setActiveTab("tracksuits")}
            className={`w-full md:w-auto text-center whitespace-nowrap overflow-hidden text-ellipsis px-2 py-2.5 md:px-6 md:py-3 rounded-full font-body text-[11px] md:text-sm font-semibold tracking-wider uppercase transition-colors duration-300 ${
              activeTab === "tracksuits"
                ? "bg-[#009A49] text-white"
                : "border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
            }`}
            data-testid="tab-tracksuits"
          >
            <><span className="sm:hidden">{t("gallery.tabTracksuitsShort")}</span><span className="hidden sm:inline">{t("gallery.tabTracksuits")}</span></>
          </button>
          <button
            onClick={() => setActiveTab("nba")}
            className={`w-full md:w-auto text-center whitespace-nowrap overflow-hidden text-ellipsis px-2 py-2.5 md:px-6 md:py-3 rounded-full font-body text-[11px] md:text-sm font-semibold tracking-wider uppercase transition-colors duration-300 ${
              activeTab === "nba"
                ? "bg-[#009A49] text-white"
                : "border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
            }`}
            data-testid="tab-nba"
          >
            <><span className="sm:hidden">{t("gallery.tabNbaShort")}</span><span className="hidden sm:inline">{t("gallery.tabNba")}</span></>
          </button>
          <button
            onClick={() => setActiveTab("nbaLegends")}
            className={`w-full md:w-auto text-center whitespace-nowrap overflow-hidden text-ellipsis px-2 py-2.5 md:px-6 md:py-3 rounded-full font-body text-[11px] md:text-sm font-semibold tracking-wider uppercase transition-colors duration-300 ${
              activeTab === "nbaLegends"
                ? "bg-[#009A49] text-white"
                : "border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
            }`}
            data-testid="tab-nba-legends"
          >
            <><span className="sm:hidden">{t("gallery.tabNbaLegendsShort")}</span><span className="hidden sm:inline">{t("gallery.tabNbaLegends")}</span></>
          </button>
          <button
            onClick={() => setActiveTab("f1")}
            className={`w-full md:w-auto text-center whitespace-nowrap overflow-hidden text-ellipsis px-2 py-2.5 md:px-6 md:py-3 rounded-full font-body text-[11px] md:text-sm font-semibold tracking-wider uppercase transition-colors duration-300 ${
              activeTab === "f1"
                ? "bg-[#009A49] text-white"
                : "border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
            }`}
            data-testid="tab-f1"
          >
            <><span className="sm:hidden">{t("gallery.tabF1Short")}</span><span className="hidden sm:inline">{t("gallery.tabF1")}</span></>
          </button>
        </motion.div>
        
        {/* Current Season Grid */}
        {activeTab === "current" && (
          <div 
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
            data-testid="current-season-grid"
          >
            {currentSeason.map((item, index) => (
              <JerseyCard
                key={item.club}
                item={item}
                index={index}
                inView={inView}
                bottomLabel={t("gallery.seasonPlayer")}
                price={40}
                imgAlt={`${t("alts.football")} ${item.club} 26/27`}
                onSelect={() => handleRequest(`${item.club} 26/27`, "football")}
              />
            ))}
          </div>
        )}

        {/* Retro Grid */}
        {activeTab === "retro" && (
          <div 
            className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6"
            data-testid="retro-grid"
          >
            {retroLegends.map((item, index) => (
              <JerseyCard
                key={item.player}
                item={item}
                index={index}
                inView={inView}
                badge={t("gallery.retro")}
                badgeRight={item.year}
                subLabel={item.player}
                bottomLabel={t("gallery.classic")}
                price={50}
                imgAlt={`${t("alts.retro")} ${item.player} – ${item.club} ${item.year}`}
                onSelect={() => handleRequest(`${item.player} - ${item.club} ${item.year}`, "retro")}
              />
            ))}
          </div>
        )}

        {/* Kids Grid */}
        {activeTab === "kids" && (
          <div 
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
            data-testid="kids-grid"
          >
            {kidsKits.map((item, index) => (
              <JerseyCard
                key={item.club}
                item={item}
                index={index}
                inView={inView}
                badge={t("gallery.kidsKit")}
                badgeRight="4-14 Y"
                bottomLabel={t("gallery.shirtShorts")}
                price={40}
                imgAlt={`${t("alts.kids")} ${item.club}`}
                onSelect={() => handleRequest(`${item.club} - ${t("gallery.kidsKit")}`, "kids")}
              />
            ))}
          </div>
        )}

        {/* Tracksuits Grid */}
        {activeTab === "tracksuits" && (
          <div 
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
            data-testid="tracksuits-grid"
          >
            {tracksuits.map((item, index) => (
              <JerseyCard
                key={item.club}
                item={item}
                index={index}
                inView={inView}
                badge={t("gallery.tracksuit")}
                bottomLabel={t("gallery.topPants")}
                price={65}
                imgAlt={`${t("alts.tracksuit")} ${item.club}`}
                onSelect={() => handleRequest(`${item.club} - ${t("gallery.tracksuit")}`, "tracksuit")}
              />
            ))}
          </div>
        )}

        {/* NBA Grid */}
        {activeTab === "nba" && (
          <div 
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
            data-testid="nba-grid"
          >
            {nbaTeams.map((item, index) => (
              <JerseyCard
                key={item.club}
                item={item}
                index={index}
                inView={inView}
                badge="NBA"
                bottomLabel="Jersey"
                price={50}
                imgAlt={`${t("alts.nba")} ${item.club}`}
                onSelect={() => handleRequest(`${item.club} - NBA`, "nba")}
              />
            ))}
          </div>
        )}

        {/* NBA Legends Grid */}
        {activeTab === "nbaLegends" && (
          <div 
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
            data-testid="nba-legends-grid"
          >
            {nbaLegends.map((item, index) => (
              <JerseyCard
                key={item.club}
                item={item}
                index={index}
                inView={inView}
                badge={t("gallery.legend")}
                badgeRight={item.year}
                bottomLabel={t("gallery.legend")}
                price={60}
                imgAlt={`${t("alts.nbaLegend")} ${item.club} – ${item.country} ${item.year}`}
                onSelect={() => handleRequest(`${item.club} - ${item.country} ${item.year}`, "nbaLegends")}
              />
            ))}
          </div>
        )}

        {/* F1 Grid */}
        {activeTab === "f1" && (
          <div 
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
            data-testid="f1-grid"
          >
            {f1Teams.map((item, index) => (
              <JerseyCard
                key={item.club}
                item={item}
                index={index}
                inView={inView}
                badge="F1"
                bottomLabel={t("gallery.f1Apparel")}
                price={item.price}
                imgAlt={`${t("alts.f1")} ${item.club}`}
                onSelect={() => handleRequest(`${item.club} - F1`, item.cat)}
              />
            ))}
          </div>
        )}
        
        {/* Bottom text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto"
        >
          <div className="px-8 py-6 border border-[#009A49]/20 rounded-sm bg-[#0A0A0A] text-center">
            <p className="text-white font-heading text-lg md:text-xl tracking-wide mb-2">
              {t("gallery.box1Title")}
            </p>
            <p className="text-zinc-500 font-body text-sm">
              {t("gallery.box1Desc")}
            </p>
          </div>
          <div className="px-8 py-6 border border-[#009A49]/20 rounded-sm bg-[#0A0A0A] text-center">
            <p className="text-white font-heading text-lg md:text-xl tracking-wide mb-2">
              {t("gallery.box2Title")}
            </p>
            <p className="text-zinc-500 font-body text-sm">
              {t("gallery.box2Desc")}
            </p>
          </div>
        </motion.div>

        {/* Beyond Jerseys banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-6 max-w-4xl mx-auto"
        >
          <div className="px-8 py-8 border border-[#009A49]/30 rounded-sm bg-gradient-to-b from-[#0A0A0A] to-[#050505] text-center">
            <p className="text-[#009A49] font-heading text-xl md:text-2xl tracking-wide mb-3">
              {t("gallery.beyondTitle")}
            </p>
            <p className="text-zinc-400 font-body text-sm md:text-base max-w-2xl mx-auto mb-6">
              {t("gallery.beyondDesc")}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {["Football Tracksuits", "Team Polos", "NBA Jerseys", "F1 Polos & Tees"].map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-2 border border-white/10 rounded-full text-zinc-300 font-body text-xs tracking-wider uppercase"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Order Modal */}
      <OrderModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        product={orderProduct}
      />

      {/* Special Order Modal */}
      <SpecialOrderModal
        isOpen={specialOpen}
        onClose={() => setSpecialOpen(false)}
      />
    </section>
  );
};
